import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Bed, EditPages, GuestList, MailingAddress, RsvpAdmin, Venue, Surprise } from '@withjoy/joykit/icons';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { PageInfo } from './useProductRows';
import type { ProductRowItemProps } from '../../PrimaryDashboardContent/ProductRowItem';
import { usePlanningRoutes } from '@apps/admin/routes/Planning/hooks/usePlanningRoutes';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

const useWeddingShopFeature = (): ProductRowItemProps | null => {
  const { value, payload } = useFeatureValue('weddingShopFeatureEnabled');
  const { t2 } = useTranslation('adminDashboardV2');

  const isFeatureEnabled = value !== 'off' && value !== undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeTileTitle = (payload as any)?.tileTitle;

  return useMemo(() => {
    if (isFeatureEnabled) {
      return {
        id: 'rsvp-weddingShop',
        title: maybeTileTitle || t2('sections').guestList.children.weddingShop.title,
        icon: Venue,
        path: 'https://weddingshop.withjoy.com',
        pathIsInJoyWeb: false,
        hasNewPill: false,
        telemetryName: 'weddingShop',
      };
    }

    return null;
  }, [isFeatureEnabled, t2, maybeTileTitle]);
};

export const useGuestListRsvpProductRow = (_eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals): ProductRow => {
  const routeHelpers = useRouterHelper();
  const hotelRoute = useHotelRoutePaths();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { guestList: guestListTranslations } = t2('sections');
  const { value: contactCollectionEnabled } = useFeatureValue('contactCollectionEnabled');
  const adminDashboardHoneymoonEnabled = useFeatureValue('adminDashboardHoneymoonEnabled').value === 'on';
  const isNewCashFundsEnabled = useFeatureValue('newCashFundsEnabled').value === 'on';
  const isHotelBlockEnabled = useFeatureValue('hotelBlockEnabled').value === 'on';
  const weddingShopTile = useWeddingShopFeature();
  const { hotelBlockPath } = usePlanningRoutes();
  const { eventHandle } = useEventInfo();

  const guestListRsvpProductRow = useMemo<ProductRow>(() => {
    const guestListStrings = guestListTranslations;
    const isEnabled = !productVerticals.optOutOfGuestList;

    const children: Array<ProductRowItemProps> = [
      {
        id: 'rsvp-guestList',
        title: guestListStrings.children.guestList.title,
        icon: GuestList,
        path: routeHelpers.buildPath(monorepoRoutePaths.guests),
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'guestList', section: 'Guest List', categoryArg }),
        telemetryName: monorepoRoutePaths.guests,
      },
      {
        id: 'rsvp-contactCollector',
        title: guestListStrings.children.contactCollector.title,
        icon: MailingAddress,
        path: routeHelpers.buildPath(adminRoutePaths.contact.path),
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'contactCollector', section: 'Contact Collector', categoryArg }),
        hasNewPill: false,
        pathIsInJoyWeb: true,
        telemetryName: adminRoutePaths.contact.path,
      },
      {
        id: 'rsvp-rsvp',
        title: guestListStrings.children.rsvp.title,
        icon: RsvpAdmin,
        path: routeHelpers.buildPath(monorepoRoutePaths.rsvp),
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'rsvp', section: 'Guest List', categoryArg }),
        telemetryName: monorepoRoutePaths.rsvp,
      },
    ];

    const moreChildren: Array<ProductRowItemProps> = [];

    if (weddingShopTile) {
      if (isNewCashFundsEnabled) {
        // When in dropdown, omit icon for a cleaner list appearance
        const { icon: _omitIcon, ...tileWithoutIcon } = weddingShopTile;
        moreChildren.push(tileWithoutIcon);
      } else {
        children.push(weddingShopTile);
      }
    }

    if (!adminDashboardHoneymoonEnabled && isHotelBlockEnabled) {
      const roomBlocksTile = {
        id: 'rsvp-hotelRoomBlock',
        title: guestListStrings.children.hotels.children.roomBlocks.title,
        hideDropdownLabel: true,
        icon: Bed,
        path: hotelBlockPath,
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'adminRoomBlockTile', section: 'Hotel Room Block', categoryArg }),
        telemetryName: PlanningRoutes.hotelblock,
        pathIsInJoyWeb: true,
      } as ProductRowItemProps;
      if (isNewCashFundsEnabled) {
        moreChildren.push(roomBlocksTile);
      } else {
        children.push(roomBlocksTile);
      }
    }

    if (adminDashboardHoneymoonEnabled) {
      const hotelSubItems = [];

      if (isHotelBlockEnabled) {
        hotelSubItems.push({
          id: 'rsvp-hotelRoomBlock-honeymoon',
          title: guestListStrings.children.hotels.children.roomBlocks.title,
          path: hotelBlockPath,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'adminRoomBlockTile', section: 'Hotel Room Block', categoryArg }),
          telemetryName: PlanningRoutes.hotelblock,
        });
      }

      if (eventHandle) {
        hotelSubItems.push({
          id: 'rsvp-honeymoon',
          title: guestListStrings.children.hotels.children.honeymoon.title,
          path: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'dashboard', utm_campaign: 'guest_list_rsvp_menu' }),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'adminHoneymoonTile', section: 'Honeymoon', categoryArg }),
          telemetryName: '/honeymoon',
        });
      }

      const hotelsParent = {
        id: 'rsvp-hotels',
        title: guestListStrings.children.hotels.title,
        hideDropdownLabel: true,
        icon: EditPages,
        tileBehavior: 'openDropdown',
        subItems: hotelSubItems,
      } as ProductRowItemProps;

      if (isNewCashFundsEnabled) {
        // flatten into More menu rather than nesting Hotels submenu under More
        moreChildren.push(...hotelSubItems);
      } else {
        children.push(hotelsParent);
      }
    }

    if (isNewCashFundsEnabled) {
      // Add Manage Collections entry
      moreChildren.unshift({
        id: 'rsvp-manageCollections',
        title: tq`Cash Funds`,
        path: routeHelpers.buildPath(adminRoutePaths.cashfunds.path),
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'manageCollections', section: 'Cash Funds', categoryArg }),
        telemetryName: adminRoutePaths.cashfunds.path,
        pathIsInJoyWeb: true,
        hasNewPill: true,
      });

      children.push({
        id: 'rsvp-rsvpMore',
        title: tq`More`,
        hideDropdownLabel: true,
        icon: Surprise,
        tileBehavior: 'openDropdown',
        subItems: moreChildren,
      });
    }

    return {
      title: guestListStrings.title,
      isEnabled,
      children: contactCollectionEnabled ? children : children.filter(({ title }) => title !== guestListStrings.children.contactCollector.title),
    };
  }, [
    guestListTranslations,
    productVerticals,
    routeHelpers,
    weddingShopTile,
    adminDashboardHoneymoonEnabled,
    contactCollectionEnabled,
    telemetry,
    hotelBlockPath,
    hotelRoute.hotelSearch,
    eventHandle,
    isHotelBlockEnabled,
    isNewCashFundsEnabled,
    tq,
  ]);

  return guestListRsvpProductRow;
};
